import React from 'react';
import styled from 'styled-components';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import { Link, useNavigate } from 'react-router-dom';
import { clearFetchReportsFailure } from 'actions';
import { useDispatch } from 'react-redux';
import DOMPurify from 'isomorphic-dompurify';
import { PATHS } from '../../constants';

const isNewError = (errorName: string) =>
  ['errors.reportNotFound'].includes(errorName);

const StyledLoginErrorNotification = styled(M.ActionableNotification)`
  &.cds--actionable-notification.cds--actionable-notification--error {
    max-width: 100%;
    border: 0;
    margin: 28px 0;
  }
  .cds--actionable-notification__icon {
    margin: 0.15rem 0.65rem 0 0;
    width: 16px;
    height: 16px;
  }
  .cds--actionable-notification__details {
    margin: 0 24px 0 15px;
    padding: 18px 0 30px;
    .cds--actionable-notification__text-wrapper {
      padding: 0;
    }
    .cds--actionable-notification__title {
      padding-bottom: 9px;
      font-size: 14px;
      line-height: 17px;
    }
    .cds--actionable-notification__subtitle {
      font-size: 14px;
      line-height: 17px;
    }
  }
`;

const LoginErrorNotification: React.FC<{
  fetchError: string;
  loginSource?: string;
}> = ({ fetchError, loginSource = 'LoginForm' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const anchorClickHandler = React.useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      navigate(PATHS.WORKOS_LOGIN);
      dispatch(clearFetchReportsFailure());
    },
    [navigate, dispatch],
  );
  if (!fetchError) return null;
  if (isNewError(fetchError) && loginSource !== 'Privacy') {
    const [preText, postText] = i18n
      .getStr(`components.${loginSource}.${fetchError}.text`)
      .split('{{Link}}');
    return (
      <StyledLoginErrorNotification
        hideCloseButton
        closeOnEscape={false}
        hasFocus={false}
        kind='error'
        className='login__error-message'
        title={i18n.getStr(`components.${loginSource}.${fetchError}.title`)}
        subtitle={
          <>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(preText),
              }}
            />
            <Link to={PATHS.WORKOS_LOGIN} onClick={anchorClickHandler}>
              {i18n.getStr(`components.${loginSource}.${fetchError}.linkText`)}
            </Link>
            <span
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(postText),
              }}
            />
          </>
        }
      />
    );
  }
  return (
    <StyledLoginErrorNotification
      hideCloseButton
      closeOnEscape={false}
      hasFocus={false}
      kind='error'
      className='login__error-message'
      title={i18n.getStr(`components.${loginSource}.errorTitle`)}
      subtitle={
        <span
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              i18n.getStr(`components.${loginSource}.${fetchError}`),
            ),
          }}
        />
      }
    />
  );
};

export default LoginErrorNotification;
